import * as Sentry from '@sentry/react';
import { Button, Skeleton, notification } from 'antd';
import { default as Axios, default as axios } from 'axios';
import {
  setLogoutAction
} from 'common/commonRedux/auth/actions';
import { authSlice } from 'common/commonRedux/auth/slice';
import { serviceWorkerPopUp } from 'common/utility/_serviceWorker';
import { MainLayout } from 'layout';
import { ForgetPassword } from 'modules/auth/ForgetPassword';
import BillingFailed from 'modules/auth/signUp/BillingFailled';
import BillingSuccess from 'modules/auth/signUp/BillingSuccess';
import SignUpComponent from 'modules/auth/signUp/SignUpComponent';
import SignUpSuccess from 'modules/auth/signUp/SignUpSuccess';
import ProfileSettings from 'modules/dashboard/UpdateOrgProfile';
import React, { Suspense, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom';

// Scoped to the routed page content (sidebar/topbar are siblings, not
// children, of this boundary — see MainLayout — so a page-level crash no
// longer blanks the whole app behind the generic root fallback).
const PageErrorFallback = ({ resetError }: { resetError: () => void }) => (
  <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 p-6 text-center">
    <h2 className="text-base font-semibold">Something went wrong loading this page</h2>
    <p className="text-sm text-[#94A3B8]">Please try again, or navigate to another page.</p>
    <Button type="primary" onClick={resetError}>Retry</Button>
  </div>
);
const AiChatPages = React.lazy(
  (): any => import('routes/modulesRoutes/AiChatPages')
);
const SettingPages = React.lazy(
  (): any => import('routes/modulesRoutes/SettingPages')
);
const ProjectManagementPages = React.lazy(
  (): any => import('routes/modulesRoutes/ProjectManagementPages')
);
const LoginForm = React.lazy((): any => import('modules/auth/login'));
const DashboardPages = React.lazy(
  (): any => import('routes/modulesRoutes/DashboardPages')
);
const MonitoringPages = React.lazy(
  (): any => import('routes/modulesRoutes/MonitoringPages')
)
const EmployeeManagementPages = React.lazy(
  (): any => import('routes/modulesRoutes/EmployeeManagementPages')
)

const AccountManagementPages = React.lazy(
  (): any => import('routes/modulesRoutes/AccountManagementPages')
)
const ReportsPage = React.lazy(
  (): any => import('routes/modulesRoutes/ReportsPage')
);
const OrganizationManagementPages = React.lazy(
  (): any => import('routes/modulesRoutes/OrganizationManagementPages')
);
const SystemSupportPages = React.lazy(
  (): any => import('routes/modulesRoutes/SystemSupportPages')
);
const TimeTrackingPages = React.lazy(
  (): any => import('routes/modulesRoutes/TimeTrackingPages')
);
const ConfigurationPages = React.lazy(
  (): any => import('routes/modulesRoutes/ConfigurationPages')
);
const EmployeeSelfRegistration = React.lazy(
  (): any => import('modules/EmployeeManagement/EmployeeList/EmployeeSelfRegistration/EmployeeSelfRegistration')
);

const NotFound = React.lazy((): any => import('common/components/notFound'));

function BasePage() {
  const { isAuth, profileData, selectedBusinessUnit } = useSelector(
    (state: any) => state?.auth,
    shallowEqual
  );
  const { refreshToken, token } = profileData
  // console.log(refreshToken)
  const encodedRefreshToken = encodeURIComponent(refreshToken)
  const { setLogin } = authSlice.actions
  const dispatch: any = useDispatch()
  const navigate = useNavigate();
  const { pathname } = useLocation();

  axios.defaults.headers.common[
    'Authorization'
  ] = `Bearer ${token}`;
  axios.defaults.headers.common['Others'] = {
    ...profileData,
    ...selectedBusinessUnit,
  };

  Axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          if (!encodedRefreshToken) {
            dispatch(setLogoutAction())
            return Promise.reject(error);
          }
          const response = await Axios.get(`/api/Auth/RefreshToken?token=${encodedRefreshToken}`);

          // console.log("response ===== ", response?.data?.token)
          dispatch(setLogin({ token: response?.data?.token }));
          const newAccessToken = response?.data?.token;
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return Axios(originalRequest);

        } catch (refreshError) {
          console.log("I am called!!")
          dispatch(setLogoutAction())
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );


  const openNotification = () => {
    notification.open({
      message: 'Your cookies not allowed',
      onClick: () => { },
    });
  };

  useEffect(() => {
    if (!navigator.cookieEnabled) {
      openNotification();
      dispatch(setLogoutAction());
      navigate('/login');
    }
  }, [isAuth]);

  // Stop scrolling on input type number
  document?.addEventListener('wheel', (e: any) => {
    if (e?.target?.type === 'number') {
      e?.target?.blur();
    }
  });

  // Remove leading zero from input type number
  document?.addEventListener('focusout', (e: any) => {
    if (e?.target?.type === 'number') {
      e.target.value = e?.target?.value?.replace(
        /^0*([0-9]+(?:\.[0-9]+)?)$/,
        '$1'
      );
    }
  });
  // call the service worker update function
  serviceWorkerPopUp()
  return (
    <MainLayout>
      <Suspense
        fallback={
          <div>
            <Skeleton loading={true} active paragraph={{ rows: 10 }}>
              Loading...
            </Skeleton>
          </div>
        }
      >
        {isAuth ?
          <Sentry.ErrorBoundary key={pathname} fallback={PageErrorFallback}>
          <Routes>
            <Route path="/" element={<DashboardPages />} />
            <Route path="/login" element={<Navigate to='/' replace />} />
            <Route path="/employee-management/*" element={<EmployeeManagementPages />} />
            <Route path='/time-tracking/*' element={<TimeTrackingPages />} />
            <Route path='/configuration/*' element={<ConfigurationPages />} />
            <Route path='/settings/*' element={<SettingPages />} />
            <Route path="/monitoring/*" element={<MonitoringPages />} />
            <Route path="/reports/*" element={<ReportsPage />} />
            <Route path="/organization-management/*" element={<OrganizationManagementPages />} />
            <Route path="/system-support/*" element={<SystemSupportPages />} />
            <Route path="/manage-account/*" element={<AccountManagementPages />} />
            <Route path="/Success" element={<Navigate to='/manage-account/payment/Success' replace />} />
            <Route path="/Canceled" element={<Navigate to='/manage-account/payment/Cancelled' replace />} />
            <Route path="/Failed" element={<Navigate to='/manage-account/payment/Failed' replace />} />
            <Route path="/account-settings" element={<ProfileSettings />} />
            <Route path="/:id/:packageName/signup" element={<Navigate to="/" replace />} />
            <Route path="*" element={<NotFound />} />
            <Route path="/manage-project/*" element={<ProjectManagementPages />} />
            <Route path="/ai-chat/*" element={<AiChatPages />} />
          </Routes>
          </Sentry.ErrorBoundary> :
          <Sentry.ErrorBoundary key={pathname} fallback={PageErrorFallback}>
          <Routes>
            {/* <Route path="/test" element={<TestDummy />} /> */}
            <Route path="/manage-account/payment/Success" element={<BillingSuccess />} />
            <Route path="/manage-account/payment/Cancelled" element={<BillingFailed />} />
            <Route path="/manage-account/payment/Failed" element={<BillingFailed />} />
            <Route
              path="/"
              element={
                !isAuth ? <Navigate to="/login" replace /> : <DashboardPages />
              }
            />
            <Route
              path="/login"
              element={isAuth ? <Navigate to="/" replace /> : <LoginForm />}
            />
            <Route
              path="/employee-self-registration"
              element={<EmployeeSelfRegistration />}
            />
            <Route
              path="/forgot-password"
              element={isAuth ? <Navigate to="/" replace /> : <ForgetPassword />}
            />
            <Route
              path="/signup"
              element={isAuth ? <Navigate to="/" replace /> : <SignUpComponent />}
            />
            <Route
              path={`/:packageId/:packageName/signup`}
              element={isAuth ? <Navigate to="/" replace /> : <SignUpComponent />}
            />
            {/* <Route
              path={`/undefined/undefined/signup`}
              element={isAuth ? <Navigate to="/" replace /> : <Navigate to="/login" replace />}
            /> */}
            <Route
              path={`/:packageId/:packageName/signup/success`}
              element={isAuth ? <Navigate to="/" replace /> : <SignUpSuccess />}
            />
            {/* <Route
              path={`/:billingId/:ammount/signup/billing`}
              element={isAuth ? <Navigate to="/" replace /> : <BillingPage />}
            /> */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Sentry.ErrorBoundary>}
      </Suspense>
    </MainLayout>
  );
}
export default BasePage;

